import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bell, Play, Clock, Plus, Trash2, CheckCircle2, XCircle, 
  Loader2, Zap, ChevronDown, ChevronUp, Send, Settings2,
  MessageSquare, AlertTriangle, BarChart3, Calendar, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { cobranzaService } from '@/lib/api';

const TRIGGERS = {
  before_expiry: { label: 'Antes del vencimiento', color: 'text-amber-600 bg-amber-50' },
  on_expiry: { label: 'El dia del vencimiento', color: 'text-red-600 bg-red-50' },
  after_expiry: { label: 'Despues del vencimiento', color: 'text-rose-600 bg-rose-50' },
};

const CHANNELS = {
  ultramsg: { label: 'WhatsApp (UltraMSG)', icon: MessageSquare },
  respondio: { label: 'WhatsApp (Respond.io)', icon: Send },
};

const TARGETS = {
  all: 'Todos los clientes',
  pending: 'Solo pendientes',
  overdue: 'Solo vencidos',
};

const DEFAULT_TEMPLATES = {
  before_expiry: 'Hola {nombre}, te recordamos que tenes una deuda de {monto_quita} con vencimiento el {vencimiento}. Consulta y paga online en: {link}',
  on_expiry: 'Hola {nombre}, hoy vence tu deuda de {monto_quita}. No pierdas el descuento. Paga ahora en: {link}',
  after_expiry: 'Hola {nombre}, tu deuda de {monto} esta vencida. Regulariza tu situacion en: {link}',
};

const formatDate = (d) => {
  if (!d) return '-';
  const parts = d.split('T')[0].split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
};

const CobranzaPanel = () => {
  const [workflows, setWorkflows] = useState([]);
  const [stats, setStats] = useState(null);
  const [scheduler, setScheduler] = useState({ enabled: false, run_hour: 9, run_minute: 0 });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [expandedWorkflow, setExpandedWorkflow] = useState(null);

  // New workflow form
  const [newName, setNewName] = useState('');
  const [newTarget, setNewTarget] = useState('all');
  const [newSteps, setNewSteps] = useState([
    { channel: 'ultramsg', trigger: 'before_expiry', days: 3, template: DEFAULT_TEMPLATES.before_expiry, enabled: true },
  ]);

  const loadData = useCallback(async () => {
    try {
      const [wfRes, statsRes, schedRes] = await Promise.all([
        cobranzaService.getWorkflows(),
        cobranzaService.getStats(),
        cobranzaService.getScheduler(),
      ]);
      setWorkflows(wfRes.data || []);
      setStats(statsRes.data);
      setScheduler(schedRes.data);
    } catch (e) { /* first load */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Ingresa un nombre'); return; }
    if (newSteps.length === 0) { toast.error('Agrega al menos un paso'); return; }
    try {
      await cobranzaService.createWorkflow({ name: newName, steps: newSteps, target: newTarget, active: true });
      toast.success('Flujo creado');
      setShowCreate(false);
      setNewName(''); setNewSteps([{ channel: 'ultramsg', trigger: 'before_expiry', days: 3, template: DEFAULT_TEMPLATES.before_expiry, enabled: true }]);
      loadData();
    } catch (e) { toast.error('Error al crear flujo'); }
  };

  const handleToggle = async (wf) => {
    try {
      await cobranzaService.updateWorkflow(wf.id, { active: !wf.active });
      loadData();
    } catch (e) { toast.error('Error'); }
  };

  const handleDelete = async (id) => {
    try {
      await cobranzaService.deleteWorkflow(id);
      toast.success('Flujo eliminado');
      loadData();
    } catch (e) { toast.error('Error'); }
  };

  const handleExecute = async () => {
    setExecuting(true);
    try {
      await cobranzaService.execute();
      toast.success('Ejecucion iniciada en segundo plano');
      setTimeout(loadData, 3000);
    } catch (e) { toast.error('Error al ejecutar'); }
    setExecuting(false);
  };

  const handleSaveScheduler = async () => {
    try {
      await cobranzaService.updateScheduler(scheduler);
      toast.success('Programacion guardada');
    } catch (e) { toast.error('Error'); }
  };

  const loadLogs = async () => {
    try {
      const res = await cobranzaService.getLogs({ limit: 100 });
      setLogs(res.data.logs || []);
    } catch (e) { /* ignore */ }
  };

  const addStep = () => {
    setNewSteps([...newSteps, { channel: 'ultramsg', trigger: 'on_expiry', days: 0, template: DEFAULT_TEMPLATES.on_expiry, enabled: true }]);
  };

  const updateStep = (idx, field, value) => {
    const updated = [...newSteps];
    updated[idx] = { ...updated[idx], [field]: value };
    // Auto-fill template if trigger changes
    if (field === 'trigger' && DEFAULT_TEMPLATES[value]) {
      updated[idx].template = DEFAULT_TEMPLATES[value];
      if (value === 'on_expiry') updated[idx].days = 0;
    }
    setNewSteps(updated);
  };

  const removeStep = (idx) => setNewSteps(newSteps.filter((_, i) => i !== idx));

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="cobranza-panel">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-teal-600" /> Cobranza Automatizada
          </h3>
          <p className="text-sm text-slate-500">Configura flujos de recordatorios automaticos por WhatsApp</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setShowLogs(true); loadLogs(); }} data-testid="view-logs-btn">
            <Clock className="w-4 h-4 mr-1" /> Historial
          </Button>
          <Button variant="outline" onClick={() => setShowScheduler(true)} data-testid="scheduler-btn">
            <Settings2 className="w-4 h-4 mr-1" /> Programar
          </Button>
          <Button onClick={handleExecute} disabled={executing} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="execute-now-btn">
            {executing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Ejecutar ahora
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{stats.total_runs || 0}</p>
            <p className="text-xs text-slate-500">Ejecuciones</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-teal-600">{stats.total_sent || 0}</p>
            <p className="text-xs text-slate-500">Mensajes enviados</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-red-500">{stats.total_failed || 0}</p>
            <p className="text-xs text-slate-500">Fallidos</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-xs text-slate-400 mb-1">Ultima ejecucion</p>
            <p className="text-sm font-medium text-slate-700">{stats.last_run ? formatDate(stats.last_run.created_at) : '-'}</p>
          </Card>
        </div>
      )}

      {/* Daily Trend */}
      {stats?.daily_trend?.length > 0 && (
        <Card className="p-4">
          <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Ultimos 7 dias
          </p>
          <div className="flex items-end gap-1 h-20">
            {stats.daily_trend.map((d, i) => {
              const maxVal = Math.max(...stats.daily_trend.map(x => x.sent + x.failed), 1);
              const h = Math.max(((d.sent + d.failed) / maxVal) * 100, 4);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex flex-col items-center" style={{ height: '64px', justifyContent: 'flex-end' }}>
                    {d.failed > 0 && <div className="w-full bg-red-400 rounded-t" style={{ height: `${(d.failed / maxVal) * 64}px`, minHeight: '2px' }} />}
                    <div className="w-full bg-teal-500 rounded-t" style={{ height: `${(d.sent / maxVal) * 64}px`, minHeight: '2px' }} />
                  </div>
                  <span className="text-[9px] text-slate-400">{d.date.slice(8)}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Workflows */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-700">Flujos de cobranza</h4>
        <Button variant="outline" size="sm" onClick={() => setShowCreate(true)} data-testid="create-workflow-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo flujo
        </Button>
      </div>

      {workflows.length === 0 ? (
        <Card className="p-12 text-center">
          <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-2">No hay flujos de cobranza</p>
          <p className="text-sm text-slate-400 mb-4">Crea tu primer flujo para automatizar recordatorios</p>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-2" /> Crear flujo
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {workflows.map(wf => (
            <Card key={wf.id} className="overflow-hidden" data-testid={`workflow-${wf.id}`}>
              <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedWorkflow(expandedWorkflow === wf.id ? null : wf.id)}>
                <div className="flex items-center gap-3">
                  <Switch checked={wf.active} onCheckedChange={() => handleToggle(wf)} onClick={e => e.stopPropagation()} />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{wf.name}</p>
                    <p className="text-xs text-slate-500">{wf.steps?.length || 0} paso(s) · {TARGETS[wf.target] || wf.target}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={wf.active ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'}>{wf.active ? 'Activo' : 'Inactivo'}</Badge>
                  {expandedWorkflow === wf.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>
              {expandedWorkflow === wf.id && (
                <div className="border-t px-4 pb-4 pt-3 bg-slate-50 space-y-2">
                  {wf.steps?.map((step, i) => {
                    const tr = TRIGGERS[step.trigger] || TRIGGERS.on_expiry;
                    return (
                      <div key={i} className="bg-white rounded-lg p-3 border border-slate-200 text-sm">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={tr.color + ' text-xs'}>{tr.label}{step.trigger !== 'on_expiry' ? ` (${step.days} dias)` : ''}</Badge>
                          <Badge variant="outline" className="text-xs">{CHANNELS[step.channel]?.label || step.channel}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 break-words">{step.template}</p>
                      </div>
                    );
                  })}
                  <div className="flex justify-end pt-2">
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(wf.id)} data-testid={`delete-workflow-${wf.id}`}>
                      <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Create Workflow Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="create-workflow-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-teal-600" /> Nuevo flujo de cobranza</DialogTitle>
            <DialogDescription>Configura los pasos automaticos del flujo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Nombre del flujo *</label>
              <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Ej: Recordatorio pre-vencimiento" data-testid="workflow-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Clientes objetivo</label>
              <Select value={newTarget} onValueChange={setNewTarget}>
                <SelectTrigger data-testid="workflow-target-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los clientes</SelectItem>
                  <SelectItem value="pending">Solo pendientes</SelectItem>
                  <SelectItem value="overdue">Solo vencidos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-slate-700">Pasos</label>
                <Button variant="outline" size="sm" onClick={addStep} data-testid="add-step-btn">
                  <Plus className="w-3 h-3 mr-1" /> Paso
                </Button>
              </div>
              <div className="space-y-3">
                {newSteps.map((step, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-slate-50 space-y-2" data-testid={`step-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-500">Paso {idx + 1}</span>
                      {newSteps.length > 1 && (
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeStep(idx)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={step.trigger} onValueChange={v => updateStep(idx, 'trigger', v)}>
                        <SelectTrigger className="text-xs h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="before_expiry">Antes del vencimiento</SelectItem>
                          <SelectItem value="on_expiry">El dia del vencimiento</SelectItem>
                          <SelectItem value="after_expiry">Despues del vencimiento</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={step.channel} onValueChange={v => updateStep(idx, 'channel', v)}>
                        <SelectTrigger className="text-xs h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ultramsg">UltraMSG</SelectItem>
                          <SelectItem value="respondio">Respond.io</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {step.trigger !== 'on_expiry' && (
                      <div>
                        <label className="text-xs text-slate-500">Dias</label>
                        <Input type="number" min={1} max={90} value={step.days} onChange={e => updateStep(idx, 'days', parseInt(e.target.value) || 0)} className="h-8 text-sm" />
                      </div>
                    )}
                    <div>
                      <label className="text-xs text-slate-500">Mensaje (variables: {'{nombre}'}, {'{monto}'}, {'{monto_quita}'}, {'{vencimiento}'}, {'{link}'}, {'{empresa}'})</label>
                      <Textarea value={step.template} onChange={e => updateStep(idx, 'template', e.target.value)} rows={3} className="text-xs mt-1" data-testid={`step-template-${idx}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Button className="w-full" onClick={handleCreate} data-testid="save-workflow-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Crear flujo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scheduler Dialog */}
      <Dialog open={showScheduler} onOpenChange={setShowScheduler}>
        <DialogContent className="max-w-sm" data-testid="scheduler-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-teal-600" /> Programar ejecucion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex items-center justify-between p-3 bg-teal-50 border border-teal-200 rounded-lg">
              <div>
                <p className="font-medium text-teal-900 text-sm">Ejecucion diaria automatica</p>
                <p className="text-xs text-teal-700">Los flujos activos se ejecutan todos los dias</p>
              </div>
              <Switch checked={scheduler.enabled} onCheckedChange={v => setScheduler(s => ({...s, enabled: v}))} data-testid="scheduler-toggle" />
            </div>
            {scheduler.enabled && (
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Hora de ejecucion (UTC)</label>
                <div className="flex gap-2 items-center">
                  <Input type="number" min={0} max={23} value={scheduler.run_hour} onChange={e => setScheduler(s => ({...s, run_hour: parseInt(e.target.value) || 0}))} className="w-20" data-testid="scheduler-hour" />
                  <span className="text-slate-500">:</span>
                  <Input type="number" min={0} max={59} step={15} value={scheduler.run_minute} onChange={e => setScheduler(s => ({...s, run_minute: parseInt(e.target.value) || 0}))} className="w-20" data-testid="scheduler-minute" />
                  <span className="text-xs text-slate-400">hs (UTC)</span>
                </div>
              </div>
            )}
            <Button className="w-full" onClick={handleSaveScheduler} data-testid="save-scheduler-btn">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="logs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-teal-600" /> Historial de envios</DialogTitle>
          </DialogHeader>
          {logs.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No hay envios registrados</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left p-2 font-semibold text-slate-600">Fecha</th>
                    <th className="text-left p-2 font-semibold text-slate-600">Cliente</th>
                    <th className="text-left p-2 font-semibold text-slate-600">Telefono</th>
                    <th className="text-left p-2 font-semibold text-slate-600">Flujo</th>
                    <th className="text-left p-2 font-semibold text-slate-600">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="p-2 text-slate-600">{formatDate(log.created_at)}</td>
                      <td className="p-2 font-medium text-slate-800">{log.debtor_name || '-'}</td>
                      <td className="p-2 text-slate-600">{log.phone}</td>
                      <td className="p-2"><Badge variant="outline" className="text-xs">{log.workflow_name}</Badge></td>
                      <td className="p-2">
                        {log.status === 'sent' 
                          ? <Badge className="bg-teal-100 text-teal-700 text-xs">Enviado</Badge>
                          : <Badge className="bg-red-100 text-red-700 text-xs">Fallido</Badge>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CobranzaPanel;
